/**
 * OnboardingEmptyState — shown on LibraryPage when no books are visible
 * (typical first-run experience). Surfaces the three differentiators
 * (4-Layer mindmap, 9-Tier adaptive quiz, lifelong VC credentials) and
 * gives the user one or two concrete next actions.
 *
 * Studio Linen palette + serif heading + mono labels + terracotta accent
 * for primary CTA. Token-only — no hardcoded sizes.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';

interface Props {
  signedIn: boolean;
  authLoading: boolean;
}

export function OnboardingEmptyState({ signedIn, authLoading }: Props) {
  const { t } = useTranslation();

  return (
    <section className="onboarding-empty" aria-labelledby="onboarding-title">
      <h2 id="onboarding-title" className="onboarding-title">
        {t('onboard.welcome')}
      </h2>
      <p className="onboarding-lead">{t('onboard.welcomeSub')}</p>

      <ul className="onboarding-features">
        <li className="onboarding-feature">
          <span className="onboarding-feature-num">L1–L4</span>
          <h3 className="onboarding-feature-title">{t('onboard.featureMapTitle')}</h3>
          <p className="onboarding-feature-body">{t('onboard.featureMapBody')}</p>
        </li>
        <li className="onboarding-feature">
          <span className="onboarding-feature-num">T1–T9</span>
          <h3 className="onboarding-feature-title">{t('onboard.featureQuizTitle')}</h3>
          <p className="onboarding-feature-body">{t('onboard.featureQuizBody')}</p>
        </li>
        <li className="onboarding-feature">
          <span className="onboarding-feature-num">W3C&nbsp;VC</span>
          <h3 className="onboarding-feature-title">{t('onboard.featureCredTitle')}</h3>
          <p className="onboarding-feature-body">{t('onboard.featureCredBody')}</p>
        </li>
      </ul>

      <div className="onboarding-cta">
        {signedIn ? (
          <>
            <Link to="/upload" className="btn btn-accent">
              {t('onboard.ctaUpload')} <span className="arrow">→</span>
            </Link>
            <a
              href="#featured"
              className="btn btn-secondary"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('.book-grid')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t('onboard.ctaBrowse')}
            </a>
          </>
        ) : (
          <>
            <Link to="/login" className="btn btn-accent">
              {t('onboard.ctaSignIn')} <span className="arrow">→</span>
            </Link>
            <span className="onboarding-hint muted">{t('onboard.signedOutHint')}</span>
          </>
        )}
        {authLoading && <span className="onboarding-hint muted">…</span>}
      </div>
    </section>
  );
}
